import moment from 'moment-timezone';
import pdfMake from 'src/modules/shared/pdf/pdfMakeClient';
import {i18n} from 'src/i18n';
import FirmaService from 'src/modules/firma/firmaService';
import PerfilEmpleadoService from 'src/modules/perfilEmpleado/perfilEmpleadoService';
import SettingsService from 'src/modules/settings/settingsService';
import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {helveticaStringWidth, helveticaWidestWord} from 'src/modules/tarea/helveticaStringWidth';
import {
  findLastTermocontraccionPrimeroDelParcialId23Task,
  findPrimeroDelParcialId23Task,
  formatMedicionesParcialValue,
  MedicionesParcialId23,
  normalizeMedicionesParcialId23,
  shouldUseMedicionesParcialId23,
} from 'src/modules/tarea/medicionesParcialId23';
import {
  CONTROL_VISUAL_GOMA_TEFLON_OPTIONS,
  findPrimeroDelParcialId24Task,
  hasMedicionesParcialCheckbox,
  MedicionesParcialId24,
  normalizeMedicionesParcialId24,
  shouldUseMedicionesParcialId24,
  TIPO_EMBALAJE_OPTIONS,
} from 'src/modules/tarea/medicionesParcialId24';
import {
  buildParentAnswerPdfGrid,
  ParentFieldLayoutConfig,
  ParentFieldLayoutItem,
  remapPlastyvergParentFieldLabel,
} from 'src/modules/tarea/parentFieldLayout';
import {
  getPlastyvergParcialCriteriaItems,
  ParcialCriteriaItem,
  resolveParcialCriteriaTitulo,
} from 'src/modules/tarea/plastyvergParcialCriteria';
import {PlastyvergPdfHeaderCodeMeta, resolvePlastyvergPdfHeaderTitle} from 'src/modules/tarea/plastyvergPdfHeaderMeta';
import {
  buildParcialColumns,
  buildParcialResultMap,
  buildSubsectionHeaderSpans,
  CHILD_TASKS_WIDE_TABLE_COLUMN_THRESHOLD,
  collectProcedureAdditionalPhotos,
  collectProcedureComentarios,
  formatProcedureAnswerText,
  getExecutorSignatureColumnLabel,
  getParcialAnswerText,
  getRecordId,
  getSignaturePersonDisplayName,
  getTaskDisplayNumber,
  groupTasksByModelo,
  hasSubsectionHeaderSpans,
  isChildTasksWideTableMode,
  isParcialAnswerAlert,
  ProcedureAdditionalPhotoEntry,
  ProcedureComentarioEntry,
  shouldApplyPartialPdfFormatChanges,
  shouldUseSequentialChildTableRowNumbers,
} from 'src/modules/tarea/tareaParcialHelpers';
import {getTaskReportFolio} from 'src/modules/tarea/taskVisualLabel';
import TenantService from 'src/modules/tenant/tenantService';

const PARCIAL_ANSWER_ALERT_PDF_CELL = {fillColor: '#f8d7da', color: '#842029', bold: true};

/** Marcas de alerta/tolerancia en celdas del PDF. Desactivado de momento; volver a `true` para reactivar. */
const APPLY_PARCIAL_ANSWER_ALERT_IN_PDF = false;

const alertCellStyle = (task: any, column: any, tenantId?: string | null) =>
  APPLY_PARCIAL_ANSWER_ALERT_IN_PDF && isParcialAnswerAlert(task, column, tenantId) ? PARCIAL_ANSWER_ALERT_PDF_CELL : {};

/** En modo wide, tablas con esta cantidad de columnas o menos van lado a lado. */
const SIDE_BY_SIDE_MAX_COLUMNS = 6;

/** Dimensiones pdfMake en puntos (ancho × alto en portrait). */
const PDF_PAGE_SIZE_POINTS: Record<string, {width: number; height: number}> = {
  A4: {width: 595.28, height: 841.89},
  A3: {width: 841.89, height: 1190.55},
  A2: {width: 1190.55, height: 1683.78},
  A1: {width: 1683.78, height: 2383.94},
  A0: {width: 2383.94, height: 3370.39},
  // Chile: Carta = Letter 8.5×11 in; Oficio = 8.5×13 in (~216×330 mm).
  CARTA: {width: 612, height: 792},
  OFICIO: {width: 612, height: 936},
};

type ChildTasksPdfPageSize = keyof typeof PDF_PAGE_SIZE_POINTS;

/** Solo Plastyverg. Probar: 'A4' | 'CARTA' | 'OFICIO' | 'A3'. */
const PAGE_SIZE_PLASTYVERG: ChildTasksPdfPageSize = 'CARTA';

/** pdfMake no conoce CARTA/OFICIO; esos van como {width, height}. */
const toPdfMakePageSize = (pageSize: ChildTasksPdfPageSize) =>
  pageSize === 'CARTA' || pageSize === 'OFICIO' ? PDF_PAGE_SIZE_POINTS[pageSize] : pageSize;

const getPageDimensions = (pageSize: ChildTasksPdfPageSize, pageOrientation: 'portrait' | 'landscape') => {
  const size = PDF_PAGE_SIZE_POINTS[pageSize] || PDF_PAGE_SIZE_POINTS.A4;
  if (pageOrientation === 'landscape') {
    return {width: size.height, height: size.width};
  }
  return size;
};

/** Descarga una imagen (firma) y la convierte a dataURL para pdfMake. */
const fetchImageAsDataUrl = async (url): Promise<string | null> => {
  if (!url) return null;
  if (typeof url === 'string' && url.startsWith('data:')) return url;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
};

const getQuestionRawLabel = (item: any, fallback: string) =>
  item?.pregunta?.label || item?.pregunta?.titulo || item?.label || item?.titulo || fallback;

const getQuestionLabel = (item: any, fallback: string) => remapPlastyvergParentFieldLabel(getQuestionRawLabel(item, fallback));

const getQuestionKey = (item: any, index: number) =>
  item?.pregunta?.id || item?.id || `${getQuestionRawLabel(item, 'pregunta')}-${item?.tipoRespuesta || ''}-${index}`;

const getProcedureQuestions = (task: any) =>
  (task?.listaProcedimiento || [])
    .map((item, index) => ({item, index}))
    .filter(({item}) => item?.tipo === 'pregunta' || item?.pregunta)
    .map(({item, index}) => ({
      key: getQuestionKey(item, index),
      label: getQuestionLabel(item, i18n('entities.firma.parcial.blankQuestion')),
      answer: formatProcedureAnswerText(item, '-'),
    }));

const getSignatureSource = (signature: any) =>
  signature?.firma || signature?.firmaRef?.downloadUrl || signature?.firmaRef?.publicUrl || '';

const getExecutorSignature = (task: any) => {
  const executorId = getRecordId(task?.ejecutor);
  const signed = (task?.firmas || []).filter((signature) => signature?.firmada && getSignatureSource(signature));
  return signed.find((signature) => executorId && getRecordId(signature?.responsable) === executorId) || null;
};

const CHILD_TASKS_TABLE_FONT_SIZE = 7;

const buildParentAnswerCard = (question: {label: string; answer: string}) => ({
  stack: [
    {text: question.label, fontSize: 6.5, color: '#666666', margin: [0, 0, 0, 2]},
    {text: question.answer || '-', fontSize: CHILD_TASKS_TABLE_FONT_SIZE},
  ],
  margin: [4, 4, 4, 4],
});

const buildParentAnswerGrid = (parentTask: any, layoutRaw: string[] | ParentFieldLayoutItem[] | ParentFieldLayoutConfig) =>
  buildParentAnswerPdfGrid(getProcedureQuestions(parentTask), layoutRaw, {
    whenEmptyUseAll: true,
    buildCard: buildParentAnswerCard,
  });

const PDF_TABLE_BORDER_COLOR = '#d0d0d0';

const parentAnswersTableLayout = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => PDF_TABLE_BORDER_COLOR,
  vLineColor: () => PDF_TABLE_BORDER_COLOR,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

/** Espacio horizontal entre columnas de mediciones (pt). Subir para más aire; 4 era el original. */
const MEDICIONES_COLUMN_GAP = 14;
/** Espacio vertical entre filas de mediciones (pt). Solo entre filas, no al borde. */
const MEDICIONES_ROW_GAP = 8;

const medicionesInnerGridLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: (i, node) => (i === node.table.widths.length - 1 ? 0 : MEDICIONES_COLUMN_GAP),
  paddingTop: () => 0,
  paddingBottom: (i, node) => (i === node.table.body.length - 1 ? 0 : MEDICIONES_ROW_GAP),
};

const wrapMedicionesPdfSection = (block: any) => ({
  unbreakable: true,
  table: {
    widths: ['*'],
    body: [[block]],
  },
  layout: parentAnswersTableLayout,
  margin: [0, 12, 0, 0],
});

const fetchTenantLogo = async () => {
  const [tenant, settings] = await Promise.all([TenantService.findCurrent(), SettingsService.find()]);
  const logoUrl = settings?.logosPdf?.[0]?.publicUrl || settings?.logos?.[0]?.publicUrl;
  const logo = logoUrl ? await fetchImageAsDataUrl(logoUrl) : null;
  return {tenant, logo};
};

const buildHeaderDateCard = (date: string, verticalMargin: number) => ({
  stack: [
    {text: i18n('common.date'), fontSize: 6.5, color: '#666666', margin: [0, 0, 0, 2], alignment: 'left'},
    {text: date, fontSize: CHILD_TASKS_TABLE_FONT_SIZE, alignment: 'right'},
  ],
  margin: [4, verticalMargin, 14, verticalMargin],
});

/** Cabecera derecha Plastyverg: etiquetas y valores en columnas separadas. */
const buildPlastyvergHeaderCodeCard = (meta: PlastyvergPdfHeaderCodeMeta, verticalMargin: number) => {
  const labelStyle = {fontSize: 6.5, color: '#666666'};
  const valueStyle = {fontSize: CHILD_TASKS_TABLE_FONT_SIZE};
  const row = (label: string, value: string) => [
    {text: label, ...labelStyle, margin: [0, 1, 2, 1]},
    {text: `: ${value}`, ...valueStyle, margin: [0, 1, 0, 1]},
  ];

  return {
    table: {
      widths: ['auto', 'auto'],
      body: [row('Código', meta.codigo), row('Versión', meta.version), row('Fecha', meta.fechaVersion)],
    },
    layout: 'noBorders',
    margin: [4, verticalMargin, 10, verticalMargin],
  };
};

/** Alto mínimo de cabecera. La fila crece si logo/contenido lo requieren. */
const PDF_HEADER_ROW_MIN_HEIGHT = Math.round(20);
const PAGE_MARGINS = {top: 70, bottom: 32, left: 18, right: 18};
/** Padding interno de la cabecera repetida (vive en el margen top). */
const PAGE_HEADER_MARGIN = {top: 8, bottom: 6};
/** Padding interno del pie de página (vive en el margen bottom). */
const PAGE_FOOTER_MARGIN = {top: 16, bottom: 2};

const getParcialFirmaSource = (parcial: any) =>
  parcial?.firma || parcial?.firmaRef?.downloadUrl || parcial?.firmaRef?.publicUrl || '';

const resolveResponsablePerfilEmpleadoId = (responsable: any, tenantId?: string | null) => {
  const directId = getRecordId(responsable?.perfilEmpleado);
  if (directId) {
    return directId;
  }

  const tenantMembership = (responsable?.tenants || []).find(
    (item: any) => !tenantId || getRecordId(item?.tenant) === String(tenantId),
  );
  return getRecordId(tenantMembership?.perfilEmpleado);
};

const hydrateParcialResponsableProfileForPdf = async (parcial: any, tenantId?: string | null) => {
  const responsable = parcial?.responsable;
  const perfilEmpleadoId = resolveResponsablePerfilEmpleadoId(responsable, tenantId);
  if (!responsable || !perfilEmpleadoId) {
    return parcial;
  }

  try {
    const perfilEmpleado = await PerfilEmpleadoService.find(perfilEmpleadoId);
    return {
      ...parcial,
      responsable: {
        ...responsable,
        perfilEmpleado,
      },
    };
  } catch {
    return parcial;
  }
};

export type ChildTasksTablePdfParcialSource = {
  /** Firma parcial explícita (p. ej. export desde un parcial seleccionado). */
  parcial?: any | null;
  /** Parciales del padre; si no hay `parcial`, se usa la última firmada. */
  parciales?: any[];
};

/** Resuelve qué firma parcial mostrar en el pie del PDF tabulado. */
export const resolveChildTasksTableParcialSignature = (source: ChildTasksTablePdfParcialSource = {}) => {
  if (source.parcial) {
    return source.parcial;
  }

  const signed = (source.parciales || []).filter((parcial) => parcial?.firmada);
  if (!signed.length) return null;

  return [...signed].sort((left, right) => {
    const leftDate = moment(left?.fechaFirmada || left?.updatedAt || left?.createdAt).valueOf();
    const rightDate = moment(right?.fechaFirmada || right?.updatedAt || right?.createdAt).valueOf();
    return rightDate - leftDate;
  })[0];
};

const getValidatorJobTitle = (responsable: any) =>
  responsable?.perfilEmpleado?.puesto?.nombre ||
  responsable?.puesto?.nombre ||
  responsable?.puesto?.label ||
  responsable?.cargo ||
  '';

const buildPlastyvergFixedCriteriaSection = (items: ParcialCriteriaItem[]) => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          stack: items.map((item, index) => ({
            text: [{text: `${item.title} `, bold: true}, {text: item.body}],
            fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
            margin: [0, index === 0 ? 0 : 3, 0, 0],
          })),
          margin: [8, 8, 8, 8],
        },
      ],
    ],
  },
  layout: parentAnswersTableLayout,
  margin: [0, 10, 0, 0],
});

const getParcialVerifierNote = (parcial: any) => String(parcial?.nota || '').trim();

const buildVerifierNoteLine = (nota: string) => ({
  text: `Verificador: ${nota}`,
  fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
  margin: [0, 4, 0, 0],
});

const buildObservacionesFooterBlock = (
  observaciones: ProcedureComentarioEntry[],
  showEmptyMessage = false,
  verifierNote = '',
) => {
  const hasVerifierNote = Boolean(verifierNote);
  const noteLine = hasVerifierNote ? [buildVerifierNoteLine(verifierNote)] : [];

  if (!observaciones.length) {
    if (showEmptyMessage) {
      return {
        stack: [
          {
            text: i18n('entities.tarea.answerTableObservaciones'),
            fontSize: 6.5,
            color: '#666666',
            margin: [0, 0, 0, 4],
          },
          {
            text: 'Sin comentarios.',
            fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
          },
          ...noteLine,
        ],
        margin: [8, 8, 8, 8],
      };
    }
    if (hasVerifierNote) {
      return {
        stack: noteLine,
        margin: [8, 8, 8, 8],
      };
    }
    return {text: '', margin: [8, 8, 8, 8]};
  }

  return {
    stack: [
      {
        text: i18n('entities.tarea.answerTableObservaciones'),
        fontSize: 6.5,
        color: '#666666',
        margin: [0, 0, 0, 4],
      },
      ...observaciones.map((entry, index) => {
        const prefix = entry.contextLabel || String(entry.rowNumber);
        return {
          text: `${prefix}: ${entry.comentario}`,
          fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
          margin: [0, index === 0 ? 0 : 2, 0, 0],
        };
      }),
      ...noteLine,
    ],
    margin: [8, 8, 8, 8],
  };
};

const ADDITIONAL_PHOTOS_PER_ROW = 4;
const ADDITIONAL_PHOTO_FIT: [number, number] = [120, 90];

const getAdditionalPhotoUrl = (photo: any) =>
  photo?.publicUrl || photo?.downloadUrl || (typeof photo === 'string' ? photo : null);

/** Sección de additionalPhotos: hasta 4 por fila, cada una con prefijo de fila como observaciones. */
const buildAdditionalPhotosSection = async (entries: ProcedureAdditionalPhotoEntry[]) => {
  if (!entries?.length) return null;

  const cells = await Promise.all(
    entries.map(async (entry) => {
      const prefix = entry.contextLabel || String(entry.rowNumber);
      const dataUrl = await fetchImageAsDataUrl(getAdditionalPhotoUrl(entry.photo));
      if (!dataUrl) {
        return {
          stack: [
            {text: `${prefix}:`, fontSize: 6.5, color: '#666666', margin: [0, 0, 0, 2]},
            {text: '-', fontSize: CHILD_TASKS_TABLE_FONT_SIZE},
          ],
          margin: [4, 4, 4, 4],
        };
      }
      return {
        stack: [
          {text: `${prefix}:`, fontSize: 6.5, color: '#666666', margin: [0, 0, 0, 2]},
          {image: dataUrl, fit: ADDITIONAL_PHOTO_FIT, alignment: 'center'},
        ],
        margin: [4, 4, 4, 4],
      };
    }),
  );

  const rows: any[] = [];
  for (let index = 0; index < cells.length; index += ADDITIONAL_PHOTOS_PER_ROW) {
    const row: any = cells.slice(index, index + ADDITIONAL_PHOTOS_PER_ROW);
    while (row.length < ADDITIONAL_PHOTOS_PER_ROW) {
      row.push({text: ''});
    }
    rows.push(row);
  }

  return {
    stack: [
      {
        text: i18n('entities.tarea.answerTableImages'),
        fontSize: 8,
        bold: true,
        margin: [0, 0, 0, 4],
      },
      {
        table: {
          widths: Array(ADDITIONAL_PHOTOS_PER_ROW).fill('*'),
          body: rows,
        },
        layout: parentAnswersTableLayout,
      },
    ],
    margin: [0, 10, 0, 0],
  };
};

const buildMedicionesPdfValueCell = (value: unknown, notApplicable?: boolean) => ({
  text: formatMedicionesParcialValue(value, notApplicable),
  fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
  alignment: 'center' as const,
  margin: [2, 3, 2, 3],
});

const buildMedicionesPdfLabelCell = (label: string) => ({
  text: label,
  fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
  bold: true,
  margin: [2, 3, 2, 3],
});

const formatMedicionesPdfTitleText = (title: string) => String(title || '').replace(/:\s*$/, '');

const medicionesNoBorderHeaderLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

const buildMedicionesPdfTitle = (title: string, subtitle?: string) => ({
  stack: [
    {text: formatMedicionesPdfTitleText(title), fontSize: 7, bold: true, margin: [0, 0, 0, subtitle ? 1 : 3]},
    ...(subtitle ? [{text: subtitle, fontSize: 6, color: '#666666', margin: [0, 0, 0, 3]}] : []),
  ],
});

const buildMedicionesParcialId23PdfBlock = (mediciones?: MedicionesParcialId23 | null) => {
  const values = normalizeMedicionesParcialId23(mediciones);

  const termocontraccionBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.termocontraccion')),
      {
        table: {
          widths: [28, '*'],
          body: [
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.termocontraccionT')),
              buildMedicionesPdfValueCell(values.termocontraccion_t, Boolean(values.na_termocontraccion)),
            ],
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.termocontraccionL')),
              buildMedicionesPdfValueCell(values.termocontraccion_l, Boolean(values.na_termocontraccion)),
            ],
          ],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const cofWidths = [42, '*', '*'];
  const cofBlock = {
    stack: [
      {
        table: {
          widths: cofWidths,
          body: [
            [
              {
                text: formatMedicionesPdfTitleText(i18n('entities.tarea.medicionesParcial.cof')),
                fontSize: 7,
                bold: true,
                margin: [0, 0, 0, 3],
              },
              {
                text: i18n('entities.tarea.medicionesParcial.estatico'),
                fontSize: 7,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 3],
              },
              {
                text: i18n('entities.tarea.medicionesParcial.dinamico'),
                fontSize: 7,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 3],
              },
            ],
          ],
        },
        layout: medicionesNoBorderHeaderLayout,
      },
      {
        table: {
          widths: cofWidths,
          body: [
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.interno')),
              buildMedicionesPdfValueCell(values.cof_interno_estatico, Boolean(values.na_cof)),
              buildMedicionesPdfValueCell(values.cof_interno_dinamico, Boolean(values.na_cof)),
            ],
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.externo')),
              buildMedicionesPdfValueCell(values.cof_externo_estatico, Boolean(values.na_cof)),
              buildMedicionesPdfValueCell(values.cof_externo_dinamico, Boolean(values.na_cof)),
            ],
          ],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const tensionBlock = {
    stack: [
      buildMedicionesPdfTitle(
        i18n('entities.tarea.medicionesParcial.tensionBobinado'),
        //i18n('entities.tarea.medicionesParcial.tensionBobinadoHint'),
      ),
      {
        table: {
          widths: [42, '*'],
          body: [
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.inicio')),
              buildMedicionesPdfValueCell(values.tension_bobinado_inicio, Boolean(values.na_tension_bobinado)),
            ],
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.termino')),
              buildMedicionesPdfValueCell(values.tension_bobinado_termino, Boolean(values.na_tension_bobinado)),
            ],
          ],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const corrienteBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.corrienteEstatica')),
      {
        table: {
          widths: [42, '*'],
          body: [
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.inicio')),
              buildMedicionesPdfValueCell(values.corriente_estatica_inicio, Boolean(values.na_corriente_estatica)),
            ],
            [
              buildMedicionesPdfLabelCell(i18n('entities.tarea.medicionesParcial.termino')),
              buildMedicionesPdfValueCell(values.corriente_estatica_termino, Boolean(values.na_corriente_estatica)),
            ],
          ],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const extraTextBlock = (titleKey: string, value: any) => ({
    stack: [
      buildMedicionesPdfTitle(i18n(titleKey)),
      {
        table: {
          widths: ['*'],
          body: [[buildMedicionesPdfValueCell(value)]],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  });

  return {
    table: {
      widths: ['*', '*', '*', '*', 'auto'],
      body: [
        [
          extraTextBlock('entities.tarea.medicionesParcial.productoAFabricar', values.producto_a_fabricar),
          extraTextBlock('entities.tarea.medicionesParcial.maquina', values.maquina),
          extraTextBlock('entities.tarea.medicionesParcial.microperforado', values.microperforado),
          extraTextBlock('entities.tarea.medicionesParcial.colorFunda', values.color_funda),
          extraTextBlock('entities.tarea.medicionesParcial.soportaconos', values.soportacono),
        ],
        [
          termocontraccionBlock,
          cofBlock,
          tensionBlock,
          corrienteBlock,
          extraTextBlock('entities.tarea.medicionesParcial.pesoRollo', values.peso_rollo),
        ],
      ],
    },
    layout: medicionesInnerGridLayout,
    margin: [8, 8, 8, 8],
  };
};

const buildMedicionesCheckboxBox = (checked: boolean) => ({
  stack: [
    {text: checked ? '√' : ' ', margin: [1.1, 0, 0, -8.8], fontSize: 8},
    {
      canvas: [
        {
          type: 'polyline',
          lineWidth: 1,
          closePath: true,
          points: [
            {x: 0, y: 0},
            {x: 8, y: 0},
            {x: 8, y: 8},
            {x: 0, y: 8},
          ],
        },
      ],
    },
  ],
  width: 10,
  margin: [0, 1, 4, 1],
});

const buildMedicionesCheckboxList = (
  options: readonly string[],
  labels: Record<string, string>,
  selected: string[] | null | undefined,
) => ({
  columns: options.map((option) => ({
    columns: [
      buildMedicionesCheckboxBox(hasMedicionesParcialCheckbox(selected, option)),
      {
        text: labels[option] || option,
        fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
        margin: [0, 2, 0, 0],
        width: '*',
      },
    ],
    margin: [0, 1, 0, 1],
  })),
  margin: [2, 2, 2, 2],
});

const buildMedicionesParcialId24PdfBlock = (mediciones?: MedicionesParcialId24 | null) => {
  const values = normalizeMedicionesParcialId24(mediciones);
  const controlVisualLabels = {
    INICIO: i18n('entities.tarea.medicionesParcial.inicio'),
    TERMINO: i18n('entities.tarea.medicionesParcial.termino'),
  };
  const tipoEmbalajeLabels = {
    SACO: i18n('entities.tarea.medicionesParcial.saco'),
    BOLSA_FUNDA: i18n('entities.tarea.medicionesParcial.bolsaFunda'),
  };

  const troquelBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.troquel')),
      {
        table: {
          widths: ['*'],
          body: [[buildMedicionesPdfValueCell(values.troquel)]],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const perforacionesBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.cantPerforaciones')),
      {
        table: {
          widths: ['*'],
          body: [[buildMedicionesPdfValueCell(values.cant_perforaciones, Boolean(values.na_cant_perforaciones))]],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  };

  const controlVisualBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.controlVisualGomaTeflon')),
      values.na_control_visual_goma_teflon
        ? {
            table: {
              widths: ['*'],
              body: [[buildMedicionesPdfValueCell(null, true)]],
            },
            layout: parentAnswersTableLayout,
          }
        : buildMedicionesCheckboxList(CONTROL_VISUAL_GOMA_TEFLON_OPTIONS, controlVisualLabels, values.control_visual_goma_teflon),
    ],
  };

  const embalajeBlock = {
    stack: [
      buildMedicionesPdfTitle(i18n('entities.tarea.medicionesParcial.tipoEmbalaje')),
      values.na_tipo_embalaje
        ? {
            table: {
              widths: ['*'],
              body: [[buildMedicionesPdfValueCell(null, true)]],
            },
            layout: parentAnswersTableLayout,
          }
        : buildMedicionesCheckboxList(TIPO_EMBALAJE_OPTIONS, tipoEmbalajeLabels, values.tipo_embalaje),
    ],
  };

  const extraTextBlock = (titleKey: string, value: any) => ({
    stack: [
      buildMedicionesPdfTitle(i18n(titleKey)),
      {
        table: {
          widths: ['*'],
          body: [[buildMedicionesPdfValueCell(value)]],
        },
        layout: parentAnswersTableLayout,
      },
    ],
  });

  /*const microPrepicadoBlock = {
    table: {
      widths: ['*', 'auto'],
      body: [
        [
          extraTextBlock('entities.tarea.medicionesParcial.microperforado', values.microperforado),
          extraTextBlock('entities.tarea.medicionesParcial.prepicado', values.na_prepicado ? 'N/A' : values.prepicado),
        ],
      ],
    },
    layout: microperforadoInnerGridLayout,
  };*/

  return {
    table: {
      widths: ['*', '*', '*', '*', 'auto'],
      body: [
        [
          troquelBlock,
          perforacionesBlock,
          extraTextBlock('entities.tarea.medicionesParcial.maquinaSelladora', values.maquina_selladora),
          extraTextBlock('entities.tarea.medicionesParcial.microperforado', values.microperforado),
          extraTextBlock('entities.tarea.medicionesParcial.prepicado', values.na_prepicado ? 'N/A' : values.prepicado),
        ],
        [
          controlVisualBlock,
          embalajeBlock,
          extraTextBlock('entities.tarea.medicionesParcial.tipoSello', values.tipo_sello),
          extraTextBlock('entities.tarea.medicionesParcial.colorFunda', values.color_funda),
          '',
        ],
      ],
    },
    layout: medicionesInnerGridLayout,
    margin: [8, 8, 8, 8],
  };
};

const buildValidatorSignatureFooterTable = async (params: {
  tenant: any;
  settings: any;
  parcial: any | null;
  observaciones?: ProcedureComentarioEntry[];
  showEmptyObservacionesMessage?: boolean;
}) => {
  const responsable = params.parcial?.responsable;
  const companyRut = params.settings?.appFields?.childTasksTableView?.companyRut || '';
  const signatureImage = params.parcial ? await fetchImageAsDataUrl(getParcialFirmaSource(params.parcial)) : null;

  const infoLineStyle = {
    fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
    alignment: 'center' as const,
    color: PDF_TABLE_BORDER_COLOR,
  };

  const verifierInfoLines: any[] = [];
  const verifierDisplayName = getSignaturePersonDisplayName(responsable);
  if (verifierDisplayName) {
    verifierInfoLines.push({
      text: verifierDisplayName,
      ...infoLineStyle,
      margin: [0, 2, 0, 0],
    });
  }
  const jobTitle = getValidatorJobTitle(responsable);
  if (jobTitle) {
    verifierInfoLines.push({text: jobTitle, ...infoLineStyle});
  }

  const signatureBlock = {
    verticalAlignment: 'bottom' as const,
    stack: [
      ...(companyRut
        ? [
            {
              text: i18n('entities.tarea.validatorSignatureRut', companyRut),
              ...infoLineStyle,
            },
          ]
        : []),
      {
        alignment: 'center',
        stack: [
          signatureImage
            ? {image: signatureImage, fit: [130, 40], alignment: 'center', margin: [0, 0, 0, 1]}
            : {text: ' ', margin: [0, 18, 0, 1]},
          ...verifierInfoLines,
          {
            columns: [
              {width: '*', text: ''},
              {
                width: 150,
                canvas: [{type: 'line', x1: 0, y1: 0, x2: 150, y2: 0, lineWidth: 0.6}],
              },
              {width: '*', text: ''},
            ],
            columnGap: 0,
            margin: [0, 2, 0, 1],
          },
          {
            text: i18n('entities.tarea.validatorSignatureLabel'),
            fontSize: 6.5,
            color: '#666666',
            alignment: 'center',
          },
        ],
      },
    ],
    // Menos padding inferior para acercar línea + label al borde de la celda.
    margin: [8, 6, 12, 2],
  };

  const observacionesBlock = buildObservacionesFooterBlock(
    params.observaciones || [],
    params.showEmptyObservacionesMessage,
    getParcialVerifierNote(params.parcial),
  );

  return {
    unbreakable: true,
    table: {
      widths: ['*', 210],
      body: [[observacionesBlock, signatureBlock]],
    },
    layout: parentAnswersTableLayout,
    margin: [0, 12, 0, 0],
  };
};

const buildChildTasksPdfHeader = (params: {
  logo: string | null;
  tenantName: string;
  title: string;
  date: string;
  codeMeta?: PlastyvergPdfHeaderCodeMeta | null;
}) => {
  const cellVerticalMargin = Math.max(2, Math.round((PDF_HEADER_ROW_MIN_HEIGHT - 16) / 2));
  const rightCell = params.codeMeta
    ? buildPlastyvergHeaderCodeCard(params.codeMeta, cellVerticalMargin)
    : buildHeaderDateCard(params.date, cellVerticalMargin);

  // pdfMake alinea celdas al tope; aproximamos centro vertical del título según hermano más alto.
  const titleFontSize = 13;
  const titleLineHeight = Math.round(titleFontSize * 1.25);
  const titleText = String(params.title || '');
  const estimatedTitleLines = Math.max(1, titleText.split('\n').length, titleText.length > 55 ? 2 : 1);
  const estimatedTitleHeight = estimatedTitleLines * titleLineHeight;
  const estimatedRightHeight = params.codeMeta ? 50 : 30;
  const estimatedLogoHeight = params.logo ? 46 : 0;
  const estimatedRowHeight = Math.max(PDF_HEADER_ROW_MIN_HEIGHT, estimatedRightHeight, estimatedLogoHeight);
  const titleTopMargin = Math.max(2, Math.round((estimatedRowHeight - estimatedTitleHeight) / 2));

  return {
    layout: parentAnswersTableLayout,
    table: {
      widths: ['auto', '*', 'auto'],
      // Altura mínima; si el contenido (logo, título, etc.) necesita más, la fila crece.
      heights: [PDF_HEADER_ROW_MIN_HEIGHT],
      body: [
        [
          params.logo
            ? {image: params.logo, maxWidth: 100, maxHeight: 40, alignment: 'center', margin: [12, 6, 4, 2]}
            : {
                text: params.tenantName,
                fontSize: 9,
                alignment: 'center',
                margin: [4, cellVerticalMargin, 4, cellVerticalMargin],
              },
          {
            text: params.title,
            fontSize: titleFontSize,
            bold: true,
            alignment: 'center',
            margin: [8, titleTopMargin, 8, 2],
          },
          rightCell,
        ],
      ],
    },
    margin: [0, 0, 0, 12],
  };
};

/**
 * Padding de celdas en pdfMake.
 * `top`/`bottom` aplican a todas las filas.
 * `bodyExtraBottom` (Plastyverg) se suma solo en filas de cuerpo sin firma ejecutor.
 */
const TABLE_CELL_PADDING = {
  left: 1,
  right: 1.5,
  top: 1,
  bottom: 1,
  bodyExtraBottom: 17,
};

/** Línea vertical de la grilla (pdfMake la suma fuera de `widths`). */
const TABLE_VLINE_WIDTH = 0.35;

const toColumnPoints = (value: number) => Math.floor(value * 100) / 100;

const getTableOffsetsTotal = (columnCount: number) =>
  columnCount * (TABLE_CELL_PADDING.left + TABLE_CELL_PADDING.right) + (columnCount + 1) * TABLE_VLINE_WIDTH;

/** Parte de `floors`; el resto del ancho útil sube hacia `fulls`, primero las palabras más cortas. Si aún sobra, se reparte a partes iguales hasta llenar la página. */
const fillTowardFull = (floors: number[], fulls: number[], targetWidth: number) => {
  const widths = floors.map((width) => Math.max(width, 1));
  const budget = Math.max(targetWidth - getTableOffsetsTotal(widths.length), widths.length);
  let leftover = budget - widths.reduce((sum, width) => sum + width, 0);
  if (leftover <= 0) return widths.map((width) => toColumnPoints(width));
  const order = fulls.map((_, index) => index).sort((a, b) => fulls[a] - fulls[b]);
  for (const index of order) {
    if (leftover <= 0) break;
    const room = Math.max(0, fulls[index] - widths[index]);
    const take = Math.min(room, leftover);
    widths[index] += take;
    leftover -= take;
  }
  if (leftover > 0) {
    const share = leftover / widths.length;
    for (let index = 0; index < widths.length; index += 1) widths[index] += share;
  }
  const rounded = widths.map((width) => toColumnPoints(width));
  const drift = budget - rounded.reduce((sum, width) => sum + width, 0);
  if (rounded.length > 0 && Math.abs(drift) >= 0.01) {
    rounded[rounded.length - 1] = toColumnPoints(rounded[rounded.length - 1] + drift);
  }
  return rounded;
};

const resolveTableLayout = (columnCount: number) => {
  if (columnCount > CHILD_TASKS_WIDE_TABLE_COLUMN_THRESHOLD) return {signatureWidth: 56, signatureFit: [52, 24]};
  if (columnCount >= 14) return {signatureWidth: 64, signatureFit: [60, 28]};
  if (columnCount >= 10) return {signatureWidth: 72, signatureFit: [68, 30]};
  if (columnCount >= 7) return {signatureWidth: 80, signatureFit: [76, 34]};
  return {signatureWidth: 90, signatureFit: [86, 38]};
};

const getUsablePageWidth = (
  pageOrientation: 'portrait' | 'landscape',
  pageSize: ChildTasksPdfPageSize = 'A4',
  horizontalMargins = PAGE_MARGINS.left + PAGE_MARGINS.right,
) => getPageDimensions(pageSize, pageOrientation).width - horizontalMargins;

const getChildTableRowNumber = (task: any, index: number, useSequentialNumber: boolean) =>
  useSequentialNumber ? index + 1 : getTaskDisplayNumber(task, index + 1);

const APRUEBA_RECHAZA_OBS_HEADER_PATTERN = /^\(A\)\s*APRUEBA\s*\(R\)\s*RECHAZA\s*\(\*\)\s*OBSERVACI[OÓ]N$/i;
const APRUEBA_RECHAZA_OBS_LINES = ['(A) APRUEBA', '(R) RECHAZA', '(*) OBSERVACIÓN'] as const;
const APRUEBA_RECHAZA_OBS_MIN_WIDTH_FACTOR = 0.9;
/**
 * Resta este valor × letras de la palabra más larga al factor 1.
 * 0.01 y 10 letras → piso 0.9; el hueco se reparte hacia Helvetica, palabras cortas primero.
 */
const COLUMN_WORD_CHAR_WIDTH_PENALTY = 0.05;

const longestWordCharCount = (text: string) => {
  const words = String(text || '')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);
  return words.length === 0 ? 0 : Math.max(...words.map((word) => word.length));
};

const wordFloor = (full: number, ...texts: string[]) => {
  const chars = Math.max(0, ...texts.map(longestWordCharCount));
  return full * Math.max(0, 1 - COLUMN_WORD_CHAR_WIDTH_PENALTY * chars);
};

const normalizeHeaderLabelForMatch = (label: string) =>
  String(label || '')
    .replace(/\s+/g, ' ')
    .trim();

const isApruebaRechazaObservacionHeader = (label: string) =>
  APRUEBA_RECHAZA_OBS_HEADER_PATTERN.test(normalizeHeaderLabelForMatch(label));

const headerWordFull = (label: string) => {
  if (isApruebaRechazaObservacionHeader(label)) {
    const lineWidth = Math.max(
      ...APRUEBA_RECHAZA_OBS_LINES.map((line) => helveticaStringWidth(line, true, CHILD_TASKS_TABLE_FONT_SIZE)),
    );
    return lineWidth * APRUEBA_RECHAZA_OBS_MIN_WIDTH_FACTOR;
  }
  return helveticaWidestWord(label, true, CHILD_TASKS_TABLE_FONT_SIZE);
};

const buildQuestionHeaderCell = (label: string) => {
  if (isApruebaRechazaObservacionHeader(label)) {
    return {
      stack: APRUEBA_RECHAZA_OBS_LINES.map((line) => ({
        text: line,
        bold: true,
        noWrap: true,
        alignment: 'center' as const,
      })),
      alignment: 'center' as const,
    };
  }
  return {text: label, bold: true, alignment: 'center' as const};
};

const buildContentFitColumnWidths = (
  columns: any[],
  rows: any[],
  _minSignatureWidth: number,
  pageOrientation: 'portrait' | 'landscape',
  headerLabels: {number: string; signature: string},
  useSequentialRowNumbers = false,
  pageSize: ChildTasksPdfPageSize = 'A4',
  targetWidth?: number,
  includeSignature = true,
  emptyAnswerText = '-',
  tenantId?: string | null,
) => {
  const usableWidth = targetWidth ?? getUsablePageWidth(pageOrientation, pageSize);
  const bodyWordFull = (text: string) => helveticaWidestWord(text, false, CHILD_TASKS_TABLE_FONT_SIZE);

  const numberTexts = [
    headerLabels.number,
    ...rows.map((row, index) => String(getChildTableRowNumber(row, index, useSequentialRowNumbers))),
  ];
  const numberFull = Math.max(
    headerWordFull(headerLabels.number),
    ...rows.map((row, index) => bodyWordFull(String(getChildTableRowNumber(row, index, useSequentialRowNumbers)))),
  );
  const numberFloor = wordFloor(numberFull, ...numberTexts);
  const signatureFull = headerWordFull(headerLabels.signature);
  const signatureFloor = wordFloor(signatureFull, headerLabels.signature);

  if (columns.length === 0) {
    return fillTowardFull(
      includeSignature ? [numberFloor, signatureFloor] : [numberFloor],
      includeSignature ? [numberFull, signatureFull] : [numberFull],
      usableWidth,
    );
  }

  const questionFulls = columns.map((column) => {
    const headerFull = headerWordFull(column.label);
    const bodyFull = Math.max(
      0,
      ...rows.map((row) =>
        bodyWordFull(getParcialAnswerText(row, column, emptyAnswerText, {includeAgregados: false, tenantId})),
      ),
    );
    return Math.max(headerFull, bodyFull);
  });
  const questionFloors = columns.map((column, index) =>
    isApruebaRechazaObservacionHeader(column.label)
      ? questionFulls[index]
      : wordFloor(
          questionFulls[index],
          column.label,
          ...rows.map((row) => getParcialAnswerText(row, column, emptyAnswerText, {includeAgregados: false, tenantId})),
        ),
  );

  const floors = includeSignature ? [numberFloor, ...questionFloors, signatureFloor] : [numberFloor, ...questionFloors];
  const fulls = includeSignature ? [numberFull, ...questionFulls, signatureFull] : [numberFull, ...questionFulls];
  return fillTowardFull(floors, fulls, usableWidth);
};

const buildChildTasksTableLayout = (
  headerRowCount: number,
  /** Índices de fila del body (0-based dentro del body) que reciben padding bottom extra. */
  bodyRowIndexesWithExtraBottom?: Set<number>,
) => ({
  hLineWidth: (i) => (i === 0 ? 0.7 : 0.35),
  vLineWidth: () => TABLE_VLINE_WIDTH,
  hLineColor: () => '#b8b8b8',
  vLineColor: () => PDF_TABLE_BORDER_COLOR,
  paddingLeft: () => TABLE_CELL_PADDING.left,
  paddingRight: () => TABLE_CELL_PADDING.right,
  paddingTop: () => TABLE_CELL_PADDING.top,
  paddingBottom: (rowIndex) => {
    const bodyIndex = rowIndex - headerRowCount;
    const extra = bodyIndex >= 0 && bodyRowIndexesWithExtraBottom?.has(bodyIndex) ? TABLE_CELL_PADDING.bodyExtraBottom : 0;
    return TABLE_CELL_PADDING.bottom + extra;
  },
});

const invisibleGridLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 2,
  paddingRight: () => 2,
  paddingTop: () => 0,
  paddingBottom: () => 4,
};

const buildChildGroupTableBlock = async (params: {
  grupo: {modelo?: any; inspecciones: any[]};
  columns: any[];
  showTitle: boolean;
  useSequentialRowNumbers: boolean;
  pageOrientation: 'portrait' | 'landscape';
  pageSize: ChildTasksPdfPageSize;
  targetWidth?: number;
  showSubsectionHeaders?: boolean;
  applyBodyExtraPadding?: boolean;
  forceIncludeSignatureColumn?: boolean;
  applyPdfFormatChanges?: boolean;
  tenantId?: string | null;
}) => {
  const {
    grupo,
    columns,
    showTitle,
    useSequentialRowNumbers,
    pageOrientation,
    pageSize,
    targetWidth,
    showSubsectionHeaders = false,
    applyBodyExtraPadding = false,
    forceIncludeSignatureColumn = false,
    applyPdfFormatChanges = false,
    tenantId,
  } = params;
  const layout = resolveTableLayout(columns.length);
  const headerLabels = {
    number: i18n('entities.firma.parcial.number'),
    signature: getExecutorSignatureColumnLabel(tenantId),
  };

  const includeSignature =
    forceIncludeSignatureColumn || grupo.inspecciones.some((task) => getSignatureSource(getExecutorSignature(task)));
  const useSubsectionHeaders = showSubsectionHeaders && hasSubsectionHeaderSpans(columns);
  const subsectionSpans = useSubsectionHeaders ? buildSubsectionHeaderSpans(columns) : [];
  const headerRowCount = useSubsectionHeaders ? 2 : 1;

  const signatureImages = includeSignature
    ? await Promise.all(
        grupo.inspecciones.map(async (task) => fetchImageAsDataUrl(getSignatureSource(getExecutorSignature(task)))),
      )
    : [];

  // Plastyverg: padding bottom extra solo en filas sin firma (la firma es lo que da altura).
  const bodyRowIndexesWithExtraBottom = applyBodyExtraPadding
    ? new Set(grupo.inspecciones.map((_, index) => index).filter((index) => !signatureImages[index]))
    : undefined;

  const questionHeaderCells = columns.map((c) => buildQuestionHeaderCell(c.label));
  const centeredCell = applyPdfFormatChanges ? {alignment: 'center' as const} : {};
  const numberHeaderCell = {text: headerLabels.number, bold: true, ...centeredCell};
  const signatureHeaderCell = includeSignature ? [{text: headerLabels.signature, bold: true, ...centeredCell}] : [];

  let headerRows: any[][];
  if (useSubsectionHeaders) {
    const subsectionHeaderCells = subsectionSpans.flatMap((span) => [
      {
        text: span.label,
        bold: true,
        alignment: 'center' as const,
        colSpan: span.colSpan,
      },
      ...Array.from({length: Math.max(0, span.colSpan - 1)}, () => ({})),
    ]);

    headerRows = [
      [
        {...numberHeaderCell, rowSpan: 2},
        ...subsectionHeaderCells,
        ...(includeSignature ? [{text: headerLabels.signature, bold: true, rowSpan: 2, ...centeredCell}] : []),
      ],
      [{}, ...questionHeaderCells, ...(includeSignature ? [{}] : [])],
    ];
  } else {
    headerRows = [[numberHeaderCell, ...questionHeaderCells, ...signatureHeaderCell]];
  }

  const bodyRows = grupo.inspecciones.map((task, index) => {
    const executorSignature = getExecutorSignature(task);
    const inspectorDisplayName =
      getSignaturePersonDisplayName(executorSignature?.responsable) || getSignaturePersonDisplayName(task?.ejecutor);

    return [
      {
        text: String(getChildTableRowNumber(task, index, useSequentialRowNumbers)),
        ...centeredCell,
      },
      ...columns.map((column) => ({
        text: getParcialAnswerText(task, column, applyPdfFormatChanges ? 'N/A' : '-', {
          includeComentario: false,
          includeAdditionalPhotos: false,
          tenantId,
        }),
        ...centeredCell,
        ...alertCellStyle(task, column, tenantId),
      })),
      ...(includeSignature
        ? [
            signatureImages[index]
              ? {
                  stack: [
                    {image: signatureImages[index], fit: layout.signatureFit, alignment: 'center', margin: [0, 0, 0, 1]},
                    ...(inspectorDisplayName
                      ? [{text: inspectorDisplayName, fontSize: CHILD_TASKS_TABLE_FONT_SIZE, alignment: 'center' as const}]
                      : []),
                  ],
                }
              : {text: '-', ...centeredCell},
          ]
        : []),
    ];
  });

  const stack: any[] = [];
  if (showTitle) {
    stack.push({
      text: grupo.modelo?.titulo || i18n('entities.tarea.answerTableUntypedGroup'),
      style: 'groupTitle',
      margin: [0, 0, 0, 4],
    });
  }

  stack.push({
    table: {
      headerRows: headerRowCount,
      widths: buildContentFitColumnWidths(
        columns,
        grupo.inspecciones,
        layout.signatureWidth,
        pageOrientation,
        headerLabels,
        useSequentialRowNumbers,
        pageSize,
        targetWidth,
        includeSignature,
        applyPdfFormatChanges ? 'N/A' : '-',
        tenantId,
      ),
      body: [...headerRows, ...bodyRows],
    },
    layout: buildChildTasksTableLayout(headerRowCount, bodyRowIndexesWithExtraBottom),
    fontSize: CHILD_TASKS_TABLE_FONT_SIZE,
  });

  return {stack, margin: [0, showTitle ? 8 : 0, 0, 0]};
};

/**
 * Genera el PDF consolidado de una verificación parcial: cabecera de la OT padre,
 * inspecciones hijas agrupadas por modeloTarea en tablas, y la firma parcial con
 * detalle de las inspecciones que cubre.
 */
export async function exportParcialAsPdf(
  record,
  options: {pageOrientation?: 'portrait' | 'landscape'; includeSignature?: boolean; title?: string} = {},
) {
  const {pageOrientation = 'portrait', includeSignature = true, title} = options;
  const tenantId = getRecordId(record?.tenant) || record?.tenantId || null;

  const parent = record?.parentTask;
  const parentTitle = parent?.tituloTarea || parent?.titulo || parent?.modeloTarea?.titulo || '';
  const parentNumber = getTaskReportFolio(parent);
  const resultMap = buildParcialResultMap(record?.resultadosHijas || []);
  const hasResults = resultMap.size > 0;

  const content: any[] = [
    {
      text: `${parentNumber ? parentNumber + ' - ' : ''}${parentTitle}`,
      style: 'title',
      margin: [0, 0, 0, 4],
    },
    {
      text: title || i18n('entities.firma.parcial.title'),
      style: 'subtitle',
      margin: [0, 0, 0, 12],
    },
  ];

  const grupos = groupTasksByModelo(record?.tareasVinculadas);

  grupos.forEach((grupo) => {
    const columns = buildParcialColumns(grupo.inspecciones);

    content.push({
      text: grupo.modelo?.titulo || i18n('entities.modeloTarea.label'),
      style: 'groupTitle',
      margin: [0, 8, 0, 4],
    });

    const headerRow = [
      {text: i18n('entities.firma.parcial.number'), bold: true},
      ...columns.map((c) => ({text: c.label, bold: true})),
      ...(hasResults ? [{text: i18n('entities.firma.parcial.rowResult'), bold: true}] : []),
    ];

    const bodyRows = grupo.inspecciones.map((inspeccion, idx) => {
      const result = resultMap.get(getRecordId(inspeccion));
      return [
        {text: String(getTaskDisplayNumber(inspeccion, idx + 1))},
        ...columns.map((c) => ({
          text: getParcialAnswerText(inspeccion, c, '', {tenantId}),
          ...alertCellStyle(inspeccion, c, tenantId),
        })),
        ...(hasResults
          ? [
              {
                text: result
                  ? [
                      i18n(
                        result.resultado === 'Aprobada' ? 'entities.firma.parcial.approved' : 'entities.firma.parcial.rejected',
                      ),
                      result.observacion ? `: ${result.observacion}` : '',
                    ].join('')
                  : '',
              },
            ]
          : []),
      ];
    });

    content.push({
      table: {
        headerRows: 1,
        widths: [30, ...columns.map(() => '*'), ...(hasResults ? ['auto'] : [])],
        body: [headerRow, ...bodyRows],
      },
      layout: 'lightHorizontalLines',
      fontSize: 8,
    });
  });

  // Firma parcial
  const signatureUrl = record?.firma || record?.firmaRef?.downloadUrl || record?.firmaRef?.publicUrl;
  const signatureImage = includeSignature ? await fetchImageAsDataUrl(signatureUrl) : null;

  const coberturaIds = (record?.tareasVinculadas || [])
    .map((t) => getTaskReportFolio(t))
    .filter(Boolean)
    .join(', ');

  if (includeSignature) {
    content.push({
      text: i18n('common.sign'),
      style: 'groupTitle',
      margin: [0, 16, 0, 4],
    });
    content.push({
      text: i18n('entities.firma.parcial.validatorLabel', getSignaturePersonDisplayName(record?.responsable) || ''),
    });
    if (signatureImage) {
      content.push({image: signatureImage, width: 180, margin: [0, 4, 0, 4]});
    }
    if (record?.fechaFirmada) {
      content.push({
        text: moment(record.fechaFirmada).format('DD-MM-YYYY HH:mm'),
        fontSize: 9,
      });
    }
  }
  if (coberturaIds) {
    content.push({
      text: `${i18n('entities.firma.parcial.coverage')}: ${coberturaIds}`,
      fontSize: 9,
      margin: [0, 4, 0, 0],
    });
  }

  const definition = {
    content,
    pageOrientation,
    defaultStyle: {fontSize: 9},
    styles: {
      title: {fontSize: 16, bold: true},
      subtitle: {fontSize: 12, color: '#666666'},
      groupTitle: {fontSize: 11, bold: true},
    },
    pageMargins: [30, 30, 30, 40],
  };

  pdfMake.createPdf(definition as any).open();
}

export async function exportChildTasksTableAsPdf(
  params: {
    parentTask: any;
    tasks: any[];
    parentQuestionKeys: string[] | ParentFieldLayoutItem[] | ParentFieldLayoutConfig;
    title?: string;
  } & ChildTasksTablePdfParcialSource,
  options: {pageOrientation?: 'portrait' | 'landscape'} = {},
) {
  let {pageOrientation = 'landscape'} = options;
  let pageSize: ChildTasksPdfPageSize = 'A4';
  const parent = params.parentTask;
  const parentDisplayNumber = getTaskReportFolio(parent);
  const parentTitle = parent?.tituloTarea || parent?.titulo || parent?.modeloTarea?.titulo || '';
  const grupos = groupTasksByModelo(params.tasks || []);
  const headerDate = moment(parent?.fechaProgramacion || parent?.fechaEjecucion || undefined).format('DD-MM-YYYY');

  const {tenant, logo} = await fetchTenantLogo();
  const settings = await SettingsService.find();
  const isPlastyverg = isPlastyvergTenant(tenant?.id);
  const applyPdfFormatChanges = shouldApplyPartialPdfFormatChanges(tenant?.id);
  // Fuera de Plastyverg (o con varios modelos): título de la OT padre + N°.
  // Plastyverg con un solo modelo: título del modeloTarea.
  const singleModelGroup = grupos.length === 1 ? grupos[0] : null;
  const useParentTaskTitle = !isPlastyverg || !singleModelGroup;
  const headerTitle = useParentTaskTitle ? parentTitle : singleModelGroup.modelo?.titulo || parentTitle;
  const displayTitle = useParentTaskTitle && parentDisplayNumber ? `${parentDisplayNumber} - ${headerTitle}` : headerTitle;

  const parentId = getRecordId(parent);
  const parciales =
    params.parciales ??
    (parentId && params.parcial == null ? await FirmaService.listParcialesByParent(parentId).catch(() => []) : []);
  const parcialForFooter = await hydrateParcialResponsableProfileForPdf(
    resolveChildTasksTableParcialSignature({
      parcial: params.parcial,
      parciales,
    }),
    tenant?.id,
  );

  const {displayTitle: pdfHeaderTitle, codeMeta: pdfHeaderCodeMeta} = resolvePlastyvergPdfHeaderTitle(displayTitle, isPlastyverg);

  const groupColumns = grupos.map((grupo) => buildParcialColumns(grupo.inspecciones));
  const preparedGroups = grupos
    .map((grupo, index) => ({grupo, columns: groupColumns[index]}))
    .sort((left, right) => right.columns.length - left.columns.length);
  const maxColumnCount = preparedGroups.reduce((max, item) => Math.max(max, item.columns.length), 0);
  const useWideTablePage = isChildTasksWideTableMode(tenant?.id, maxColumnCount);

  // Tablas muy anchas (Blumar Calidad): A3 landscape para que quepan todas las columnas.
  if (useWideTablePage) {
    pageOrientation = 'landscape';
    pageSize = 'A3';
  } else if (isPlastyverg) {
    pageSize = PAGE_SIZE_PLASTYVERG;
  }

  const pageHeader = buildChildTasksPdfHeader({
    logo,
    tenantName: tenant?.name || '',
    title: pdfHeaderTitle,
    date: headerDate,
    codeMeta: pdfHeaderCodeMeta,
  });

  const content: any[] = [];

  const parentGrid = buildParentAnswerGrid(parent, params.parentQuestionKeys);
  if (parentGrid.body.length > 0) {
    content.push({
      table: {
        widths: Array(parentGrid.cellsPerRow).fill('*'),
        body: parentGrid.body,
      },
      layout: parentAnswersTableLayout,
      margin: [0, 0, 0, 12],
    });
  }

  const useSequentialRowNumbers = shouldUseSequentialChildTableRowNumbers(preparedGroups.length, tenant?.id);
  const showGroupTitles = preparedGroups.length > 1;
  const usablePageWidth = getUsablePageWidth(pageOrientation, pageSize);
  const sideBySideTargetWidth = (usablePageWidth - 8) / 2;
  const applyBodyExtraPadding = isPlastyvergTenant(tenant?.id);
  const forceIncludeSignatureColumn = applyBodyExtraPadding;

  const fullWidthGroups = useWideTablePage
    ? preparedGroups.filter((item) => item.columns.length > SIDE_BY_SIDE_MAX_COLUMNS)
    : preparedGroups;
  const sideBySideGroups = useWideTablePage
    ? preparedGroups.filter((item) => item.columns.length <= SIDE_BY_SIDE_MAX_COLUMNS)
    : [];

  for (const {grupo, columns} of fullWidthGroups) {
    content.push(
      await buildChildGroupTableBlock({
        grupo,
        columns,
        showTitle: showGroupTitles,
        useSequentialRowNumbers,
        pageOrientation,
        pageSize,
        showSubsectionHeaders: useWideTablePage,
        applyBodyExtraPadding,
        forceIncludeSignatureColumn,
        applyPdfFormatChanges,
        tenantId: tenant?.id,
      }),
    );
  }

  for (let index = 0; index < sideBySideGroups.length; index += 2) {
    const left = sideBySideGroups[index];
    const right = sideBySideGroups[index + 1];
    const leftBlock = await buildChildGroupTableBlock({
      grupo: left.grupo,
      columns: left.columns,
      showTitle: true,
      useSequentialRowNumbers,
      pageOrientation,
      pageSize,
      targetWidth: sideBySideTargetWidth,
      showSubsectionHeaders: useWideTablePage,
      applyBodyExtraPadding,
      forceIncludeSignatureColumn,
      applyPdfFormatChanges,
      tenantId: tenant?.id,
    });

    if (!right) {
      content.push({
        ...leftBlock,
        margin: [0, 8, 0, 0],
      });
      continue;
    }

    const rightBlock = await buildChildGroupTableBlock({
      grupo: right.grupo,
      columns: right.columns,
      showTitle: true,
      useSequentialRowNumbers,
      pageOrientation,
      pageSize,
      targetWidth: sideBySideTargetWidth,
      showSubsectionHeaders: useWideTablePage,
      applyBodyExtraPadding,
      forceIncludeSignatureColumn,
      applyPdfFormatChanges,
      tenantId: tenant?.id,
    });

    content.push({
      table: {
        widths: ['*', '*'],
        body: [
          [
            {stack: leftBlock.stack, margin: [0, 0, 4, 0]},
            {stack: rightBlock.stack, margin: [4, 0, 0, 0]},
          ],
        ],
      },
      layout: invisibleGridLayout,
      margin: [0, 8, 0, 0],
    });
  }

  const observaciones = preparedGroups.flatMap(({grupo}) =>
    collectProcedureComentarios(grupo.inspecciones, {
      useSequentialRowNumbers,
      useRowNumberContext: isPlastyvergTenant(tenant?.id),
    }),
  );

  const additionalPhotoEntries = preparedGroups.flatMap(({grupo}) =>
    collectProcedureAdditionalPhotos(grupo.inspecciones, {
      useSequentialRowNumbers,
      useRowNumberContext: isPlastyvergTenant(tenant?.id),
    }),
  );

  const primeroDelParcialId23Task = isPlastyvergTenant(tenant?.id) ? findPrimeroDelParcialId23Task(params.tasks) : null;
  const primeroDelParcialId24Task = isPlastyvergTenant(tenant?.id) ? findPrimeroDelParcialId24Task(params.tasks) : null;
  const showMedicionesId23 = Boolean(
    primeroDelParcialId23Task &&
    shouldUseMedicionesParcialId23({
      tenantId: tenant?.id,
      modeloTareaTitulo: primeroDelParcialId23Task?.modeloTarea?.titulo,
      primeroDelParcial: primeroDelParcialId23Task?.primeroDelParcial,
    }),
  );
  const showMedicionesId24 = Boolean(
    !showMedicionesId23 &&
    primeroDelParcialId24Task &&
    shouldUseMedicionesParcialId24({
      tenantId: tenant?.id,
      modeloTareaTitulo: primeroDelParcialId24Task?.modeloTarea?.titulo,
      primeroDelParcial: primeroDelParcialId24Task?.primeroDelParcial,
    }),
  );
  const showMedicionesColumn = showMedicionesId23 || showMedicionesId24;
  const medicionesKind = showMedicionesId24 ? 'id24' : showMedicionesId23 ? 'id23' : null;

  const baseMediciones = normalizeMedicionesParcialId23(primeroDelParcialId23Task?.medicionesParcial);
  const termocontraccionTask = isPlastyvergTenant(tenant?.id)
    ? findLastTermocontraccionPrimeroDelParcialId23Task(params.tasks)
    : null;
  const medicionesParcialId23 = termocontraccionTask?.medicionesParcial
    ? {
        ...baseMediciones,
        termocontraccion_t: termocontraccionTask.medicionesParcial.termocontraccion_t,
        termocontraccion_l: termocontraccionTask.medicionesParcial.termocontraccion_l,
        na_termocontraccion: termocontraccionTask.medicionesParcial.na_termocontraccion,
      }
    : baseMediciones;
  const medicionesParcial = showMedicionesId24
    ? normalizeMedicionesParcialId24(primeroDelParcialId24Task?.medicionesParcial)
    : showMedicionesId23
      ? medicionesParcialId23
      : null;

  if (isPlastyvergTenant(tenant?.id)) {
    const criteriaTitulo = resolveParcialCriteriaTitulo({parentTask: parent, tasks: params.tasks});
    const criteriaItems = getPlastyvergParcialCriteriaItems(criteriaTitulo);
    if (criteriaItems?.length) {
      content.push(buildPlastyvergFixedCriteriaSection(criteriaItems));
    }
  }

  const medicionesSection = showMedicionesColumn
    ? wrapMedicionesPdfSection(
        medicionesKind === 'id24'
          ? buildMedicionesParcialId24PdfBlock(medicionesParcial as MedicionesParcialId24 | null)
          : buildMedicionesParcialId23PdfBlock(medicionesParcial as MedicionesParcialId23 | null),
      )
    : null;

  const additionalPhotosSection = await buildAdditionalPhotosSection(additionalPhotoEntries);
  const validatorSignatureFooter = await buildValidatorSignatureFooterTable({
    tenant,
    settings,
    parcial: parcialForFooter,
    observaciones,
    showEmptyObservacionesMessage: applyPdfFormatChanges,
  });

  if (applyPdfFormatChanges) {
    if (medicionesSection) {
      content.push(medicionesSection);
    }
    content.push(validatorSignatureFooter);
    if (additionalPhotosSection) {
      content.push({...additionalPhotosSection, pageBreak: 'before'});
    }
  } else {
    if (additionalPhotosSection) {
      content.push(additionalPhotosSection);
    }
    if (medicionesSection) {
      content.push(medicionesSection);
    }
    content.push(validatorSignatureFooter);
  }

  const definition = {
    content,
    pageSize: toPdfMakePageSize(pageSize),
    pageOrientation,
    defaultStyle: {fontSize: 8},
    styles: {
      title: {fontSize: 16, bold: true},
      subtitle: {fontSize: 12, color: '#666666'},
      groupTitle: {fontSize: 11, bold: true},
    },
    pageMargins: [PAGE_MARGINS.left, PAGE_MARGINS.top, PAGE_MARGINS.right, PAGE_MARGINS.bottom],
    header: () => ({
      ...pageHeader,
      margin: [PAGE_MARGINS.left, PAGE_HEADER_MARGIN.top, PAGE_MARGINS.right, PAGE_HEADER_MARGIN.bottom],
    }),
    footer: (currentPage, pageCount) => ({
      margin: [PAGE_MARGINS.left, PAGE_FOOTER_MARGIN.top, PAGE_MARGINS.right, PAGE_FOOTER_MARGIN.bottom],
      columns: [
        {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: 'left',
          color: '#303030',
          fontSize: 7,
        },
        {
          margin: [0, 3, 0, 0],
          text: 'Generado con planiwise.com',
          alignment: 'right',
          color: '#A0A0A0',
          fontSize: 6,
        },
      ],
    }),
  };

  pdfMake.createPdf(definition as any).open();
}

const tareaParcialPdf = {exportParcialAsPdf, exportChildTasksTableAsPdf, resolveChildTasksTableParcialSignature};

export default tareaParcialPdf;
